import type { useI18n } from '@/i18n'
import { BadgeVariant } from '@/components/ui/badge'

type I18n = ReturnType<typeof useI18n>

/**
 * Base class for rich enum values that carry metadata (badge variant, i18n label,
 * semantic CSS classes) while still supporting loose equality with raw string values
 * from the backend.
 *
 * Usage:
 *   class EnforcementMode extends EnumValue {
 *       static STRICT = new EnforcementMode('STRICT', BadgeVariant.SUCCESS, (i18n) => i18n.relationshipSchema.enforcement.strict)
 *       static WARN = new EnforcementMode('WARN', BadgeVariant.WARNING, (i18n) => i18n.relationshipSchema.enforcement.warn)
 *       static OFF = new EnforcementMode('OFF', BadgeVariant.DESTRUCTIVE, (i18n) => i18n.relationshipSchema.enforcement.off)
 *   }
 *
 *   // Loose equality with backend strings works — mode arrives as 'STRICT' from JSON:
 *   mode == EnforcementMode.STRICT  // true when mode === 'STRICT'
 *
 *   // Metadata access:
 *   <Badge variant={mode.variant}>{mode.label(i18n)}</Badge>
 *
 * The `labelFn` parameter is a lambda rather than a pre-resolved string so language
 * resolution happens at call time, not at class-load time — the user can switch
 * language and labels follow.
 *
 * NOTE: Use == (loose equality), NOT === (strict equality). The
 * `RequireEnumValueLooseEquality` check enforces this.
 */
export class EnumValue {
    constructor(
        public readonly code: string,
        public readonly variant: BadgeVariant,
        private readonly labelFn?: (i18n: I18n) => string,
        public readonly borderClass?: string,
        public readonly textClass?: string,
    ) {}

    label(i18n: I18n): string {
        return this.labelFn ? this.labelFn(i18n) : this.code
    }

    valueOf(): string {
        return this.code
    }

    toString(): string {
        return this.code
    }
}
